import { useCallback, useEffect, useState } from 'react';
import { setPriceHook, store } from './data/store';
import { alertEngine, type AlertEvent } from './data/alerts';
import { pool } from './data/socket';
import { hms, price as fmtPrice } from './lib/num';
import { SplitGrid, type LayoutNode } from './ui/SplitGrid';
import { Toolbar, StatusBar, FeedNotice } from './ui/Chrome';
import { Watchlist } from './ui/Watchlist';
import { ChartPanel } from './ui/ChartPanel';
import { OrderBookPanel } from './ui/OrderBook';
import { TapePanel } from './ui/Tape';
import { AlertsPanel } from './ui/AlertsPanel';
import { usePersisted } from './ui/hooks';

const DEFAULT_LAYOUT: LayoutNode = {
  type: 'split',
  dir: 'row',
  sizes: [23, 53, 24],
  children: [
    {
      type: 'split',
      dir: 'col',
      sizes: [62, 38],
      children: [
        { type: 'leaf', key: 'watchlist' },
        { type: 'leaf', key: 'alerts' },
      ],
    },
    { type: 'leaf', key: 'chart' },
    {
      type: 'split',
      dir: 'col',
      sizes: [58, 42],
      children: [
        { type: 'leaf', key: 'book' },
        { type: 'leaf', key: 'tape' },
      ],
    },
  ],
};

export function App() {
  const [layout, setLayout] = usePersisted<LayoutNode>('terminal.layout.grid.v1', DEFAULT_LAYOUT);
  const [focused,setFocused]=useState(false);
  const [mobile,setMobile]=useState(()=>matchMedia('(max-width: 900px)').matches);
  const [panel,setPanel]=useState('chart');
  useEffect(()=>{const query=matchMedia('(max-width: 900px)');const change=()=>setMobile(query.matches);query.addEventListener('change',change);return()=>query.removeEventListener('change',change)},[]);

  // Boot the data layer exactly once. StrictMode double-invokes effects in
  // development, so `store.start()` is idempotent rather than guarded here.
  useEffect(() => {
    setPriceHook((symbol, price) => alertEngine.onPrice(symbol, price));
    store.start();
    return () => setPriceHook(null);
  }, []);

  // Warn before a reload takes the sockets down mid-session is overkill, but
  // shutting them down cleanly on unload avoids a lingering half-open socket.
  useEffect(() => {
    const onUnload = () => pool.shutdown();
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
  }, []);

  const render = useCallback((key: string) => {
    switch (key) {
      case 'watchlist':
        return <Watchlist />;
      case 'chart':
        return <ChartPanel />;
      case 'book':
        return <OrderBookPanel />;
      case 'tape':
        return <TapePanel />;
      case 'alerts':
        return <AlertsPanel />;
      default:
        return null;
    }
  }, []);

  return (
    <div className="app">
      <Toolbar focused={focused} onFocus={()=>setFocused(!focused)} onResetLayout={() => {setLayout(DEFAULT_LAYOUT);setFocused(false)}} />
      <FeedNotice />
      {mobile&&<nav className="panel-tabs" aria-label="Workspace panels">{[['chart','Chart'],['watchlist','Markets'],['book','Order book'],['tape','Trades'],['alerts','Alerts']].map(([key,label])=><button key={key} aria-pressed={key===panel} onClick={()=>setPanel(key)}>{label}</button>)}</nav>}
      <div className="workspace">
        {mobile?render(panel):focused?<ChartPanel/>:<SplitGrid layout={layout} onChange={setLayout} render={render} />}
      </div>
      <StatusBar />
      <ToastHost />
    </div>
  );
}

interface Toast extends AlertEvent {
  id: string;
}

/** Transient notifications when an alert fires. */
function ToastHost() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(
    () =>
      alertEngine.triggered.on((ev) => {
        const toast: Toast = { ...ev, id: `${ev.alert.id}-${ev.at}` };
        setToasts((prev) => [toast, ...prev].slice(0, 4));
        window.setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== toast.id));
        }, 9000);
      }),
    [],
  );

  if (toasts.length === 0) return null;

  return (
    <div className="toasts">
      {toasts.map((t) => {
        const st = store.peek(t.alert.symbol);
        const precision = st?.pricePrecision ?? 2;
        const up = t.alert.direction === 'above';
        return (
          <div
            key={t.id}
            className={`toast ${up ? 'up' : 'down'}`}
            onClick={() => {
              store.setActive(t.alert.symbol);
              setToasts((prev) => prev.filter((x) => x.id !== t.id));
            }}
          >
            <span className="toast-icon">{up ? '▲' : '▼'}</span>
            <div className="toast-body">
              <div className="toast-title">
                {t.alert.symbol} crossed {up ? 'above' : 'below'}{' '}
                {fmtPrice(t.alert.price, precision)}
              </div>
              <div className="toast-sub">
                traded {fmtPrice(t.price, precision)} · {hms(t.at)}
                {t.alert.note ? ` · ${t.alert.note}` : ''}
              </div>
            </div>
            <button
              className="toast-close"
              onClick={(e) => {
                e.stopPropagation();
                setToasts((prev) => prev.filter((x) => x.id !== t.id));
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
